import {makeAutoObservable, runInAction} from "mobx";

import {
	CheckSuccessReply,
	CompilationFailureReply,
	CompilationSuccessReply,
	Files,
	FlattenedMetadata,
	InjectorMetadata,
} from "../shared/Types";
import CsInstance from "./CsInstance";
import {runFetch} from "../util/runFetch";
import {ConsoleTabMode} from "./theaterStore";
import {Pyodide} from "./pythonRunner";

// linear state machine; can always jump to "None" or "Error"
// None -> Compiling -> Swappable -> None
export enum CompileStatus {
	None = "None",
	Compiling = "Compiling",
	Error = "Error",
}

function flattenMetadata(
	existing: FlattenedMetadata,
	metadata: InjectorMetadata[]
): FlattenedMetadata {
	// preserve old AopsTheater method IDs
	const flattened = {method_map: {...existing.method_map}} as FlattenedMetadata;

	for (const {method_map, class_name, source_file} of metadata)
		for (const [methodI, info] of Object.entries(method_map))
			flattened.method_map[methodI as unknown as number] = {
				...info,
				class_name,
				source_file,
			};

	return flattened;
}

/**
 * MobX store for compilation state.
 */
class CompileStore {
	private _compileStatus = CompileStatus.None;
	private _error = "";
	private _versionedJarData: [Uint8Array, number] = [new Uint8Array([]), -1];
	private _version = 1;
	private _mostRecentCompiledVersion = 0;
	private _hasHadSuccessfulCompile = false;
	private _serverUrl: string;
	private _abortController: AbortController;
	private _metadatas: FlattenedMetadata = {method_map: {}};

	private _csInstance: CsInstance;

	constructor(props: {csInstance: CsInstance; serverUrl: string}) {
		this._csInstance = props.csInstance;
		this._serverUrl = props.serverUrl;
		this._abortController = new AbortController();
		makeAutoObservable(this);
	}

	// methods

	/**
	 * Attempt to compile the code. If the code is already compiling or the editor
	 * hasn't changed, do nothing.
	 */
	async attemptToCompileCode() {
		const {codeStore, theaterStore} = this._csInstance;

		// If the code is already compiling or the editor hasn't changed, do nothing
		if (this.isCompiling || !codeStore.hasUncompiledChanges) return;

		theaterStore.isRunning = false;
		codeStore.hasUncompiledChanges = false;

		// Save local file list upon submitting code to the server
		codeStore.saveProjectToLocalStorage();

		await codeStore.compileCode();
	}

	/**
	 * Compiles the code.
	 * @param project - The project name.
	 * @param code - The code to compile, separated by file.
	 * @param hiddenFileNames - Hidden file names, if any.
	 */
	async compileCode(project: string, code: Files, hiddenFileNames?: string[]) {
		this.compileStatus = CompileStatus.Compiling;

		// Wait for flags to be ready so that we know if we're using Python
		await this._csInstance.featureFlagStore.flagsReady();

		try {
			let jar: Uint8Array | undefined;
			let result: {result: any} | {error: any} | undefined;
			const version = this._version++;

			// Run the code
			if (this._csInstance.featureFlagStore.usePython) {
				this.error = "";
				result = await this.runPython();
			} else {
				jar = await this.callCompileApi(
					project,
					code,
					version,
					hiddenFileNames
				);
			}

			// Handle the result
			if (this._csInstance.featureFlagStore.usePython) {
				if (result && "result" in result) {
					this.error = "";
					this.compileStatus = CompileStatus.None;
				} else {
					this.error = result?.error || "Pyodide not correctly initialized";
					this.compileStatus = CompileStatus.Error;
				}
			} else {
				if (jar) {
					this.versionedJarData = [jar, version];
					this.compileStatus = CompileStatus.None;
				} else {
					this.compileStatus = CompileStatus.Error;
				}
			}
		} catch (e) {
			console.error("failed to make API call to compile code", e);
			this.compileStatus = CompileStatus.Error;
		}

		// Finish compile
		this._csInstance.getOrCreateMessenger().pauseTheater();

		// Flags have been checked above, so we know loadCode can finish; no need to check them again

		// Automatically run tests (if they exist) on compile
		if (
			this._csInstance.testsStore.hasTests &&
			this._csInstance.testsStore.shouldRunTests
		) {
			const {theaterStore} = this._csInstance;

			theaterStore.clearLog(ConsoleTabMode.Tests);
			const result = await this._csInstance
				.getOrCreateMessenger()
				.runActivityTests(this._csInstance.codeStore.projectName || "");

			// Everything below is copied from the messenger switch block
			if (!result) {
				return;
			}

			// Compile error should take precedence over test results
			if (this.error === "") {
				theaterStore.consoleTabMode = ConsoleTabMode.Tests;
			}

			// Print output to console's tests mode
			theaterStore.addLine(
				`Your magic code is: ${result.summary.magicCode}\n`,
				ConsoleTabMode.Tests
			);
			theaterStore.addLine(
				`If this is a homework problem, don't forget to submit the magic code on your homework page!\n\n`,
				ConsoleTabMode.Tests
			);
			theaterStore.addLine(
				`${result.summary.totalPassed}/${result.summary.totalTests} Test Cases passed.\n\n`,
				ConsoleTabMode.Tests
			);
			result.resultsByCategory.forEach((category) => {
				// TODO: Add category name to the console in the future
				// theaterStore.addLine(
				// 	`Category: ${category.categoryName}\n`,
				// 	ConsoleTabMode.Tests
				// );
				category.results.forEach((res) => {
					theaterStore.addLine(res.message, ConsoleTabMode.Tests);
				});
			});

			// Allow consumers to access test results.
			this._csInstance.testsStore.testResults = result;
			this._csInstance.onTestResultsCallback(
				this._csInstance.codeStore.projectName,
				result
			);
		}
	}

	// Only run the current file's code
	async runPython() {
		// TODO: this shouldn't be here but I don't know where to put it yet
		// this usually happens when the user successfully compiles code, but we don't compile code when running python
		this._csInstance.theaterStore.clearLog();

		// Run the python code
		const pyodide = Pyodide.getInstance();
		return pyodide.run(
			this._csInstance.codeStore.fileMap[
				this._csInstance.codeStore.focusedFile!
			]?.value || ""
		);
	}

	async callDiagnosticsApi(
		code: Files,
		hiddenFileNames?: string[]
	): Promise<CheckSuccessReply | null> {
		let response: CheckSuccessReply | CompilationFailureReply;
		try {
			response = await runFetch({
				url: `${this._serverUrl}/api/v2/compileCode`,
				mode: "cors",
				method: "POST",
				securityToken: this._csInstance.securityToken,
				body: {
					project: this._csInstance.codeStore.projectName,
					code: code,
					version: 0,
					studentCodeOnly: true,
					hiddenFileNames: hiddenFileNames,
					checkOnly: true,
				},
			});
		} catch (e) {
			response = {type: "failure", error: String(e)};
		}

		if (response.type === "check_success") {
			return response;
		} else {
			return null;
		}
	}

	async callCompileApi(
		project: string,
		code: Files,
		version = 0,
		hiddenFileNames?: string[]
	) {
		this.error = "";
		// abort previous compile calls
		this._abortController.abort();
		this._abortController = new AbortController();
		let response: CompilationSuccessReply | CompilationFailureReply;
		try {
			// wait until we're certain whether bjvm is enabled
			await this._csInstance.featureFlagStore.flagsReady();
			response = await runFetch({
				url: `${this._serverUrl}/api/v2/compileCode`,
				mode: "cors",
				method: "POST",
				securityToken: this._csInstance.securityToken,
				body: {
					project: project,
					code: code,
					version,

					// If the user has had a successful compile, we only want to send the student code
					// for efficiency purposes
					studentCodeOnly: this.hasHadSuccessfulCompile,

					hiddenFileNames: hiddenFileNames,

					checkOnly: false,

					noCheerpjCompat: this._csInstance.featureFlagStore.bjvmEnabled,
				},
				signal: this._abortController.signal,
			});
		} catch (e) {
			let errorMessage = String(e);
			if (e instanceof Error) {
				try {
					errorMessage =
						JSON.parse(e.message)?.moreInfo?.errorMessage ||
						JSON.parse(e.message)?.message ||
						errorMessage; // fallback in case the above are valid but empty
				} catch (jsonE) {}
			}
			response = {type: "failure", error: "ERROR: " + errorMessage};
		}

		// If the user has compiled a newer version since this fetch started, throw
		// out this response.
		if (version < this._mostRecentCompiledVersion) return;

		let errorText: string;
		this._mostRecentCompiledVersion = version;
		if (response.type === "success") {
			this.metadatas = flattenMetadata(this.metadatas, response.metadatas);

			// https://stackoverflow.com/questions/64770762 to handle async actions in MobX
			runInAction(() => {
				this._csInstance.testsStore.hasTests = response.hasTests;
			});

			this.error = "";
			this.hasHadSuccessfulCompile = true;
			this._csInstance.theaterStore.clearLog();
			this._csInstance.theaterStore.showConsole = false;

			return new Uint8Array(
				// Convert base64 string to ArrayBuffer
				// Credit: https://stackoverflow.com/a/49273187#comment104073073_49273187
				await (
					await fetch("data:application/octet;base64," + response.jarBase64)
				).arrayBuffer()
			);
		} else {
			// Compile error case
			errorText = response.error;
		}
		this.compileStatus = CompileStatus.Error;
		this.setErrorStatusAndMessage(errorText);
	}

	get isCompiling() {
		return this.compileStatus === CompileStatus.Compiling;
	}

	setErrorStatusAndMessage(error?: string) {
		if (error) {
			this._error = error;
			this._csInstance.theaterStore.showConsole = true;
		}
	}

	// autogen getters and setters for MobX actions

	get compileStatus() {
		return this._compileStatus;
	}
	private set compileStatus(value) {
		this._compileStatus = value;
	}

	get metadatas() {
		return this._metadatas;
	}
	set metadatas(value) {
		this._metadatas = value;
	}

	get error() {
		return this._error;
	}
	set error(value) {
		this._error = value;
	}

	get versionedJarData() {
		return this._versionedJarData;
	}
	private set versionedJarData(value) {
		this._versionedJarData = value;
	}

	get hasHadSuccessfulCompile() {
		return this._hasHadSuccessfulCompile;
	}
	set hasHadSuccessfulCompile(value) {
		this._hasHadSuccessfulCompile = value;
	}

	get serverUrl() {
		return this._serverUrl;
	}

	set serverUrl(value) {
		this._serverUrl = value;
	}
}

export default CompileStore;
